import { Controller } from "@hotwired/stimulus"
import { paintTheme } from "../builder/theme_paint"
import { contrastRatio, AA_NORMAL_TEXT } from "../builder/contrast"

const SAVE_DELAY = 500

// Site theme panel.
//
// The whole theme resolves through :root custom properties, so a change is one
// setProperty on the canvas document -- no re-render, no reload, and every
// node that referenced a token follows immediately. That is the payoff of
// keeping presets token-only.
export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:ready", () => this.start(), { once: true })
    this.root.addEventListener("builder:part", () => this.paint())
    if (this.root.builder) this.start()
  }

  disconnect() { clearTimeout(this.saveTimer) }

  get builder() { return this.root.builder }
  get schema() { return this.builder.schema.theme }
  get canvasDoc() { return this.root.querySelector("iframe")?.contentDocument }

  start() {
    this.config = structuredClone(this.builder.themeValue || {})
    this.render()
    this.paint()
  }

  // --- reading and writing ------------------------------------------------

  value(group, key) {
    return this.config?.[group]?.[key] ?? this.schema.defaults[group][key]
  }

  set(group, key, value) {
    this.config[group] = this.config[group] || {}
    this.config[group][key] = value
    this.paint()
    this.render()
    this.scheduleSave()
  }

  applyPreset(event) {
    const preset = this.schema.presets.find((p) => p.key === event.currentTarget.dataset.preset)
    if (!preset) return

    this.config = structuredClone(preset.config)
    this.paint()
    this.render()
    this.scheduleSave()
  }

  scheduleSave() {
    clearTimeout(this.saveTimer)
    this.saveTimer = setTimeout(() => this.save(), SAVE_DELAY)
  }

  async save() {
    const response = await fetch(this.builder.urls.theme, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
        Accept: "application/json"
      },
      body: JSON.stringify({ theme: this.config })
    })
    if (!response.ok) return

    const payload = await response.json()
    this.config = payload.theme_config
  }

  // Writes the compiled theme straight onto the canvas :root.
  paint() {
    paintTheme(this.canvasDoc, this.schema.roles, (group, key) => this.value(group, key))
  }

  // --- rendering ----------------------------------------------------------

  render() {
    const labels = this.builder.schema.labels.ui
    const fragment = document.createDocumentFragment()

    fragment.append(this.presetsSection(labels))
    fragment.append(this.paletteSection(labels))
    fragment.append(this.typographySection(labels))
    fragment.append(this.scalesSection(labels))

    this.panelTarget.replaceChildren(fragment)
  }

  section(title) {
    const wrap = document.createElement("section")
    wrap.className = "mb-4"
    const heading = document.createElement("h3")
    heading.className = "mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400"
    heading.textContent = title
    wrap.append(heading)
    return wrap
  }

  presetsSection(labels) {
    const wrap = this.section(labels.theme_presets)
    const grid = document.createElement("div")
    grid.className = "grid grid-cols-2 gap-1.5"

    this.schema.presets.forEach((preset) => {
      const button = document.createElement("button")
      button.type = "button"
      button.dataset.preset = preset.key
      button.dataset.action = "builder-theme#applyPreset"
      button.className =
        "flex items-center gap-1.5 rounded-md border border-slate-200 px-2 py-1.5 text-[11px] " +
        "font-medium hover:border-cyan-400 dark:border-slate-700"

      const swatch = document.createElement("span")
      swatch.className = "h-3 w-3 shrink-0 rounded-full border border-black/10"
      swatch.style.background = preset.config.palette.accent

      const name = document.createElement("span")
      name.className = "truncate"
      name.textContent = preset.name

      button.append(swatch, name)
      grid.append(button)
    })

    wrap.append(grid)
    return wrap
  }

  paletteSection(labels) {
    const wrap = this.section(labels.theme_palette)

    this.schema.roles.forEach((role) => {
      const row = document.createElement("label")
      row.className = "mb-2 flex items-center gap-2"

      const swatch = document.createElement("input")
      swatch.type = "color"
      swatch.value = this.value("palette", role)
      swatch.className = "h-7 w-9 shrink-0 rounded border border-slate-300 dark:border-slate-700"
      swatch.addEventListener("input", () => this.set("palette", role, swatch.value))

      const name = document.createElement("span")
      name.className = "flex-1 text-[11px] text-slate-500"
      name.textContent = role.replace("_", " ")

      row.append(swatch, name)
      wrap.append(row)
    })

    const ink = this.value("palette", "ink")
    const bg = this.value("palette", "bg")
    const accentInk = this.value("palette", "accent_ink")
    const accent = this.value("palette", "accent")

    if (contrastRatio(ink, bg) < AA_NORMAL_TEXT || contrastRatio(accentInk, accent) < AA_NORMAL_TEXT) {
      const warning = document.createElement("p")
      warning.className = "rounded bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
      warning.textContent = labels.theme_contrast_warning
      wrap.append(warning)
    }

    return wrap
  }

  typographySection(labels) {
    const wrap = this.section(labels.theme_typography)

    ;[["heading", labels.theme_heading_font], ["body", labels.theme_body_font]].forEach(([slot, label]) => {
      const row = document.createElement("label")
      row.className = "mb-2 block"

      const caption = document.createElement("span")
      caption.className = "mb-1 block text-[11px] text-slate-500"
      caption.textContent = label

      const select = document.createElement("select")
      select.className =
        "w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-900"
      this.schema.fonts.forEach((font) => {
        const option = document.createElement("option")
        option.value = font
        option.textContent = font.replace("-", " ")
        option.selected = this.value("typography", slot) === font
        select.append(option)
      })
      select.addEventListener("change", () => this.set("typography", slot, select.value))

      row.append(caption, select)
      wrap.append(row)
    })

    return wrap
  }

  scalesSection(labels) {
    const wrap = this.section(labels.theme_scales)
    const dials = [
      ["typography", "base", labels.theme_base_size, "typography.base", 1],
      ["typography", "scale", labels.theme_scale_ratio, "typography.scale", 0.01],
      ["spacing", "unit", labels.theme_spacing_unit, "spacing.unit", 1],
      ["shape", "radius", labels.theme_radius, "shape.radius", 1]
    ]

    dials.forEach(([group, key, label, rangeKey, step]) => {
      const range = this.schema.ranges[rangeKey]
      const row = document.createElement("label")
      row.className = "mb-3 block"

      const caption = document.createElement("span")
      caption.className = "mb-1 flex items-center justify-between text-[11px] text-slate-500"
      caption.innerHTML = `<span></span><span></span>`
      caption.firstChild.textContent = label
      caption.lastChild.textContent = this.value(group, key)

      const slider = document.createElement("input")
      slider.type = "range"
      slider.className = "w-full"
      slider.min = range.min
      slider.max = range.max
      slider.step = step
      slider.value = this.value(group, key)
      slider.addEventListener("input", () => {
        caption.lastChild.textContent = slider.value
        this.setQuietly(group, key, Number(slider.value))
      })
      slider.addEventListener("change", () => this.render())

      row.append(caption, slider)
      wrap.append(row)
    })

    return wrap
  }

  // Dragging a slider repaints continuously but must not rebuild the panel on
  // every frame, which would drop the slider out from under the pointer.
  setQuietly(group, key, value) {
    this.config[group] = this.config[group] || {}
    this.config[group][key] = value
    this.paint()
    this.scheduleSave()
  }
}
